<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="main">
    <div class="left-container">
      <Menus
        :menu="menu"
        icon="https://dsmlib.logistics.systems/Icons/icons8-maintenance-64.png"
        @menuClicked="menuClicked"
      />
    </div>
    <div class="right-container">
      <TableBox
        :tableData="partInventoryTableData"
        :selectedRow.sync="partInventoryTableSelectedRows"
        :loading="partInventoryTableLoading"
        title="Part Inventory"
        :dataType="partInventoryTableDataType"
        :hideColumns="['CustRef', 'JobStatus']"
        :columnOrderViews.sync="partInventoryTableColumnOrderViews"
        :columnOrderID="partInventoryTableColumnOrderID"
      />
    </div>
    <PopupEntry
      :show.sync="popupEntryShow"
      v-if="popupEntryShow"
      :title="jobNoTitle"
      :updatedBy="lastUpdatedValue"
      @save="saveClicked"
      :requiredError="entryRequired"
      :successMessages="entrySuccess"
      :errorMessages="entryError"
      @requiredClicked="entryRequiredClicked"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
    >
      <div style="display: flex; flex-direction: column; padding: 0 5px; height: 100%">
        <DateTimePicker
          :isRequired="true"
          placeholder="Date"
          style="margin-left: 5px"
          :value.sync="jobDateValue"
          :isDisabled="viewMode"
          :errorMessage.sync="jobDateValueError"
          ref="jobdate"
        />
        <div class="flex-container">
          <div>
            <InputQuery
              placeholder="Vehicle # - Vehicle Code"
              :selectedObject.sync="vehicleNoValue"
              :items="vehicleList"
              itemKey="label"
              :isDisabled="viewMode"
              :tableBoxHideColumns="['label']"
            />
            <Input placeholder="DO #" :inputValue.sync="doNoValue" :isDisabled="viewMode" validator="text,25" />
            <InputQuery
              placeholder="Warehouse"
              :selectedObject.sync="warehouseValue"
              :items="warehouseList"
              itemKey="label"
              :isRequired="true"
              :isDisabled="viewMode"
              :tableBoxHideColumns="['label']"
              @selected="warehouseSelected"
              :errorMessage.sync="warehouseValueError"
              ref="warehouse"
            />
          </div>
          <div>
            <div style="display: flex; gap: 5px">
              <Input placeholder="Cont #" :inputValue.sync="contNoValue" :isDisabled="viewMode" validator="text,25" />
              <Dropdown
                placeholder="Size"
                style="flex: 300px"
                :selectedObject.sync="sizeValue"
                :search="sizeInputValue"
                :items="sizeList"
                itemKey="size"
                :allowFreeText="true"
                :isDisabled="viewMode"
                validator="integer,25"
              />
            </div>
            <Input placeholder="Seal #" :inputValue.sync="sealNoValue" :isDisabled="viewMode" validator="text,25" />
            <Dropdown
              placeholder="Billing Type"
              :selectedObject.sync="billingTypeValue"
              :items="billingTypeList"
              itemKey="label"
              :isDisabled="viewMode"
            />
          </div>
        </div>
        <Input placeholder="Remark" :inputValue.sync="remarkValue" :isDisabled="viewMode" validator="text,200" />
        <BoxContainer title="Product Details" style="flex: 1; min-height: 0">
          <div class="table-tab-container">
            <div style="display: flex; flex-direction: column">
              <div class="cta-group" v-if="!viewMode">
                <div class="po-import-cta" v-if="popupAction == 'N'">
                  <template v-if="purchaseOrderImportedLabel">
                    <div class="po-imported-label">{{ purchaseOrderImportedLabel }}</div>
                    <Button @click="clearPurchaseOrderImport" :disabled="partListSelectedItem">Clear Import</Button>
                  </template>
                  <template v-else>
                    <InputQuery
                      class="po-import-input"
                      placeholder="Purchase Order"
                      :selectedObject.sync="purchaseOrderValue"
                      :items="purchaseOrderList"
                      itemKey="label"
                      :tableBoxHideColumns="['label']"
                      :tableBoxDataType="purchaseOrderDataType"
                      :isLoading="purchaseOrderListLoading"
                      :errorMessage.sync="purchaseOrderValueError"
                      @focusChanged="purchaseOrderFocusChanged"
                    />
                    <Button @click="importPurchaseOrderItems" :disabled="partListSelectedItem || !purchaseOrderValue"
                      >Import from Purchase Order</Button
                    >
                  </template>
                </div>
                <div class="part-list-cta">
                  <Button @click="partFieldsNew" :disabled="partListSelectedItem">Add</Button>
                  <Button
                    @click="partDeleteClick"
                    :disabled="partListSelectedItem || partListTableSelectedRows.length == 0"
                    >Delete</Button
                  >
                </div>
              </div>
              <TableBox
                :tableData="partListTableDataFiltered"
                :selectedRow.sync="partListTableSelectedRows"
                title="Product List"
                :dataType="partListTableDataType"
                style="flex-grow: 1"
                :additionalButtons="tableAdditionalButton"
                @buttonClicked="tableRowClicked"
                :hideColumns="['DNSeqNo']"
                :columnOrderViews.sync="partListTableColumnOrderViews"
                :columnOrderID="partListTableColumnOrderID"
              />
            </div>
            <div v-if="partListSelectedItem">
              <div v-if="!viewMode" class="cta-group">
                <Button @click="tabFieldsSaveClicked">Save</Button>
                <Button @click="partFieldsCancel">Cancel</Button>
              </div>
              <div v-if="viewMode" class="cta-group">
                <Button @click="partFieldsCancel">Close</Button>
              </div>
              <InputQuery
                placeholder="Part Type"
                :selectedObject.sync="partFields.partTypeValue"
                :isDisabled="viewMode || partListSelectedItem.partUsedInJob == '1'"
                :items="partTypeListActive"
                itemKey="label"
                :tableBoxHideColumns="['label', 'Active', 'ProdTxnID', 'Unique Code', 'Expense Code', 'Is Expense']"
                :isRequired="true"
                :errorMessage.sync="partTypeValueError"
                @selected="partTypeSelected"
              />
              <div class="flex-container">
                <div>
                  <Input
                    placeholder="Serial #"
                    :isDisabled="viewMode"
                    :inputValue.sync="partFields.serialNoValue"
                    :isRequired="serialNoRequired"
                    :errorMessage.sync="serialNoValueError"
                    @inputChanged="serialNoChanged"
                  />
                  <Input
                    placeholder="Parts #"
                    :isDisabled="viewMode"
                    :inputValue.sync="partFields.partNoValue"
                    validator="text,25"
                  />
                  <Input
                    placeholder="Size"
                    :isDisabled="viewMode"
                    :inputValue.sync="partFields.sizeValue"
                    validator="text,25"
                  />
                  <div style="display: flex; gap: 5px">
                    <Input
                      placeholder="Unit Price (RM)"
                      :isDisabled="viewMode"
                      :inputValue.sync="partFields.unitPriceValue"
                      validator="currency"
                    />
                    <Input
                      placeholder="Qty"
                      style="flex: 300px"
                      :isDisabled="viewMode || serialNoRequired || partFields.serialNoValue != ''"
                      :inputValue.sync="partFields.qtyValue"
                      :errorMessage.sync="qtyValueError"
                      validator="positiveDecimal,,2"
                    />
                  </div>
                  <Input
                    placeholder="Model"
                    :isDisabled="viewMode"
                    :inputValue.sync="partFields.modelValue"
                    validator="text,25"
                  />
                  <Dropdown
                    placeholder="Location"
                    :isDisabled="viewMode"
                    :items="locationListFiltered"
                    itemKey="Location"
                    :tableBoxHideColumns="['Warehouse']"
                    :selectedObject.sync="partFields.locationValue"
                  />
                </div>
                <div>
                  <InputQuery
                    placeholder="Made in"
                    :isDisabled="viewMode"
                    :items="countryList"
                    itemKey="label"
                    :tableBoxHideColumns="['label']"
                    :selectedObject.sync="partFields.madeInValue"
                  />
                  <InputQuery
                    placeholder="Manufacturer"
                    :isDisabled="viewMode"
                    :items="manufacturerList"
                    itemKey="label"
                    :tableBoxHideColumns="['label']"
                    :selectedObject.sync="partFields.manufacturerValue"
                  />
                  <DateTimePicker
                    placeholder="Mfg Date"
                    :isDisabled="viewMode"
                    :value.sync="partFields.mfgDateValue"
                    :showTime="false"
                  />
                  <Input
                    placeholder="PO #"
                    :isDisabled="viewMode"
                    :inputValue.sync="partFields.poNoValue"
                    validator="text,25"
                  />
                  <InputQuery
                    placeholder="Supplier"
                    :isDisabled="viewMode"
                    :items="supplierList"
                    itemKey="label"
                    :tableBoxHideColumns="['label']"
                    :selectedObject.sync="partFields.supplierValue"
                  />
                  <Input
                    placeholder="Supp Invoice"
                    :isDisabled="viewMode"
                    :inputValue.sync="partFields.sppInvValue"
                    validator="text,25"
                  />
                </div>
              </div>
              <Input
                placeholder="Remark"
                :isDisabled="viewMode"
                :inputValue.sync="partFields.remarkValue"
                validator="text,200"
              />
              <div class="flex-container" @click="retreadError = ''">
                <div
                  style="display: flex; align-items: center; margin-top: 10px; margin-left: 5px"
                  v-if="tyreRetreadEnable"
                >
                  <CheckBoxV2
                    title="Retread Tyre"
                    :isChecked.sync="partFields.tyreRetreadValue"
                    style="flex-shrink: 0"
                    :isDisabled="viewMode"
                  />
                  <Input
                    placeholder="No of Retread"
                    :isDisabled="!partFields.tyreRetreadValue || viewMode"
                    :isRequired="true"
                    :inputValue.sync="partFields.noOfRetreadValue"
                    :errorMessage.sync="noOfRetreadValueError"
                  />
                </div>
              </div>
              <div
                v-if="retreadError"
                style="background-color: red; color: white; padding: 1px 5px; border-radius: 5px; display: inline-flex"
                >{{ retreadError }}</div
              >
              <div
                v-if="partListSelectedItem.partUsedInJob == '1' && !viewMode"
                style="font-style: italic; margin-top: 10px"
                >*<b>Part Type</b> cannot be edited as there are job(s) allocated with this part.</div
              >
            </div>
          </div>
        </BoxContainer>
      </div>
    </PopupEntry>
    <div class="stock-out-popup" v-if="stockOutPopupShow">
      <div class="stock-out-popup-inner">
        <div style="font-size: 1rem; font-weight: bold" class="mb-2">Stock History</div>
        <Tab
          class="tab-component"
          :tabs="tabItems"
          :tabIndex.sync="selectedTabIndex"
          @tabClicked="tabClick"
          :preventTabSwitch="true"
          style="min-height: 0; height: 100%"
          :tabsHeightToParent="true"
        >
          <div class="tab-container" :slot="stockOutTitle" style="min-height: 0; height: 100%">
            <TableBox
              style="height: 100%; min-height: 0"
              :tableData="stockOutFiltered"
              @buttonClicked="stockOutTableRowClicked"
              :additionalButtons="stockOutTableAdditionalButtons"
              title="Stock In History"
              :dataType="stockOutListDataType"
              :hideColumns="['AllocGuid']"
            />
          </div>
          <div class="tab-container" :slot="stockOutHistoryTitle" style="min-height: 0; height: 100%">
            <TableBox
              style="height: 100%; min-height: 0"
              :tableData="stockOutHistoryFiltered"
              :additionalButtons="stockOutHistoryTableAdditionalButtons"
              title="Stock Out History"
              :dataType="stockOutListDataType"
              @buttonClicked="stockOutHistoryDeleteClicked"
              :hideColumns="['AllocGuid']"
            />
          </div>
        </Tab>
        <div class="flex-container align-items-center justify-content-end">
          <button style="width: auto" @click="stockOutPopupShow = false">Close</button>
        </div>
      </div>
    </div>
    <div class="stock-out-popup" v-if="stockOutUpdatePopupShow">
      <div class="stock-out-popup-inner stock-out-action">
        <div class="flex-container align-items-center" style="flex-direction: column">
          <img src="https://dsmapi.logistics.systems/dsm6gIcons/warning.png" height="75" style="width: auto" alt="" />
          <div style="font-size: 1.3em; font-weight: bold; text-align: center">Enter the info for Stock Out.</div>
          <div>
            <DateTimePicker
              placeholder="Date"
              :isRequired="true"
              :value.sync="stockOutDateValue"
              :errorMessage.sync="stockOutDateValueError"
            />
            <div style="display: flex; gap: 8px">
              <Input
                placeholder="Quantity"
                :inputValue.sync="stockOutQuantity"
                :errorMessage.sync="stockOutQuantityError"
                :isRequired="true"
                validator="positiveDecimal"
              />
              <InputQuery
                placeholder="Transport Vehicle"
                :allowFreeText="true"
                :items="vehicleList"
                itemKey="Inventory #"
                :tableBoxHideColumns="['label']"
                :search.sync="stockOutVehicleNo"
              />
            </div>
            <Input
              placeholder="Remarks"
              :isRequired="true"
              :inputValue.sync="stockOutRemark"
              :errorMessage.sync="stockOutRemarkError"
            />
          </div>
          <div class="flex-container justify-content-end">
            <Button style="width: auto" @click="stockOutClick">Confirm</Button>
            <Button style="width: auto" @click="stockOutUpdatePopupShow = false">Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  computed: {
    partTypeListActive() {
      return this.partTypeList.filter((item) => item.Active == '1');
    },

    menu() {
      const menuEnabled = !this.partInventoryTableLoading;
      let menuItems = [
        {
          title: 'New Part Inventory',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: 'New',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuNewPartInv',
        },
        {
          title: 'Edit Part Inventory',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png',
          key: 'Edit',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuEditPartInv',
        },
        {
          title: 'Delete Part Inventory',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          key: 'Delete',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuDeletePartInv',
        },
        {
          title: 'View Part Inventory',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          key: 'View',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuViewPartInv',
        },
        {
          title: 'Print Stock Balance',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          key: 'PrintStockBalance',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuPrintStockBalance',
        },
        {
          title: 'Stock History',
          icon: 'https://dsmlib.logistics.systems/Icons/upload.png',
          key: 'StockOut',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuStockOut',
        },
      ];

      menuItems = menuItems.map((item) => {
        const { control } = item;
        const SAS = this.SASControl.find((sas) => sas.Control == control) || null;
        if (!SAS) return item;
        const { Properties, PropertiesValue } = SAS;
        return {
          ...item,
          [Properties.toLowerCase()]: PropertiesValue == 'True',
        };
      });

      return menuItems;
    },

    jobNoTitle() {
      const title =
        this.viewMode ? 'View Part Inventory'
        : this.jobNoValue ? 'Edit Part Inventory'
        : 'New Part Inventory';
      return `${title}${this.jobNoValue ? ` - ${this.jobNoValue}` : ''}`;
    },

    partListTableDataFiltered() {
      return this.partListTableData
        .filter((item) => item.action != 'D')
        .map((part, index) => {
          const {
            ProdTxnID,
            BatchNo,
            PalletNo,
            ProdDesc,
            Manufacturer,
            MadeIn,
            Size,
            UnitPrice,
            Quantity,
            CalcQtyBal,
            ProdDate,
            Date,
            Spp_Code: SppCode,
            SppInv,
            PONo,
            Remark2,
            NoOfRetread,
            BinLoc,
            DNSeqNo,
          } = part;

          const partType = this.partTypeList.find((item) => item.ProdTxnID == ProdTxnID);
          const madeIn = this.countryList.find((item) => item['Country Code'] == MadeIn);

          return {
            '#': index + 1,
            'Part Type': partType?.Description ?? '',
            'Serial No': BatchNo,
            'Part No': PalletNo ?? '',
            'Mfg Date': ProdDate ?? '',
            Manufacturer: Manufacturer ?? '',
            'Made In': madeIn?.Country ?? '',
            Model: ProdDesc ?? '',
            Qty: Quantity ? parseFloat(Quantity).toFixed(2) : '',
            QtyBal: CalcQtyBal ?? '',
            'Unit Price(RM)': UnitPrice ?? '',
            'Amount(RM)': CalcQtyBal && UnitPrice ? CalcQtyBal * UnitPrice : '0',
            'Purchase From': SppCode ?? '',
            'Date Purchase': Date ?? '',
            Location: BinLoc ?? '',
            '# Of Retread': NoOfRetread ?? '',
            Size: Size ?? '',
            Remark: Remark2 ?? '',
            'Supp Invoice': SppInv ?? '',
            'PO #': PONo ?? '',
            DNSeqNo,
          };
        });
    },

    locationListFiltered() {
      return this.warehouseValue ?
          this.locationList.filter((location) => location.Warehouse == this.warehouseValue['Warehouse Code'])
        : [];
    },

    tableAdditionalButton() {
      if (this.viewMode) return [{ text: 'View', index: 0, width: 60 }];
      return [{ text: 'Edit', index: 0, width: 60 }];
    },

    serialNoRequired() {
      const { partTypeValue } = this.partFields;
      if (!partTypeValue) return false;
      return partTypeValue?.['Unique Code'] == 'Y';
    },

    tyreRetreadEnable() {
      const { partTypeValue } = this.partFields || {};
      if (!partTypeValue) return false;
      return partTypeValue?.['Part Group'].toUpperCase() == 'TYRE' ?? false;
    },

    fieldsChanged() {
      if (this.viewMode) return false;
      let current = {};
      let saved = {};

      if (this.partListSelectedItem) {
        current = this.partFields;
        saved = this.partFieldsChangedCheck;
      }

      current = JSON.stringify(current);
      saved = JSON.stringify(saved);

      return current !== saved;
    },

    stockOutTableAdditionalButtons() {
      const stockOutUpdateSAS = Boolean(this.SASControl.find((item) => item.Control == 'mnuStockOutUpdate'));

      if (stockOutUpdateSAS) return [];
      return [{ text: 'Stock Out', index: 0, width: 100 }];
    },

    stockOutHistoryTableAdditionalButtons() {
      const stockOutDeleteSAS = Boolean(this.SASControl.find((item) => item.Control == 'mnuStockOutDelete'));

      if (stockOutDeleteSAS) return [];
      return [{ text: 'Delete', index: 0, width: 80 }];
    },

    stockOutFiltered() {
      const filtered = this.stockOutList?.map((item, index) => {
        const {
          JobNo,
          Date: JobDate,
          WHSECode,
          PartType,
          BatchNo,
          PalletNo,
          Manufacturer,
          MadeIn,
          ProdDesc,
          CalcQtyBal,
          Size,
          SppInv,
          PONo,
          AllocGuid,
        } = item;
        const { Name: WarehouseName } =
          this.warehouseList.find((warehouse) => warehouse['Warehouse Code'] == WHSECode) || {};
        const { MFGName } = this.manufacturerList.find((manufacturer) => manufacturer.MFGCode == Manufacturer) || {};
        const { Country } = this.countryList.find((country) => country['Country Code'] == MadeIn) || {};
        return {
          '#': index + 1,
          'GRN No': JobNo,
          'GRN Date': JobDate ?? '',
          'Part Type': PartType ?? '',
          'Serial No': BatchNo ?? '',
          'Qty Bal': parseFloat(CalcQtyBal).toFixed(2),
          Warehouse: WHSECode ? `${WHSECode} - ${WarehouseName}` : '',
          'Part No': PalletNo ?? '',
          Model: ProdDesc ?? '',
          Size: Size,
          Manufacturer: Manufacturer ? `${Manufacturer} - ${MFGName}` : '',
          'Made In': MadeIn ? `${MadeIn} - ${Country}` : '',
          'Supp Invoice': SppInv,
          'PO No': PONo,

          AllocGuid: AllocGuid,
        };
      });
      return filtered;
    },

    stockOutHistoryFiltered() {
      const filtered = this.stockOutHistoryList?.map((item, index) => {
        const {
          JobNo,
          Date: JobDate,
          WHSECode,
          ProdName,
          BatchNo,
          PalletNo,
          Manufacturer,
          MadeIn,
          ProdDesc,
          AssignQty,
          AllocGuid,
        } = item;
        const { Name: WarehouseName } =
          this.warehouseList.find((warehouse) => warehouse['Warehouse Code'] == WHSECode) || {};
        const { MFGName } = this.manufacturerList.find((manufacturer) => manufacturer.MFGCode == Manufacturer) || {};
        const { Country } = this.countryList.find((country) => country['Country Code'] == MadeIn) || {};
        return {
          '#': index + 1,
          'GIT No': JobNo,
          'GIT Date': JobDate ?? '',
          'Part Type': ProdName ?? '',
          'Serial No': BatchNo ?? '',
          Qty: parseFloat(AssignQty).toFixed(2),
          Warehouse: WHSECode ? `${WHSECode} - ${WarehouseName}` : '',
          'Part No': PalletNo ?? '',
          Model: ProdDesc ?? '',
          Manufacturer: Manufacturer ? `${Manufacturer} - ${MFGName}` : '',
          'Made In': MadeIn ? `${MadeIn} - ${Country}` : '',

          AllocGuid: AllocGuid,
        };
      });
      return filtered;
    },

    stockOutTitle() {
      const length = this.stockOutFiltered?.length ?? 0;
      return `Stock In History (${length})`;
    },

    stockOutHistoryTitle() {
      const length = this.stockOutHistoryFiltered?.length ?? 0;
      return `Stock Out History (${length})`;
    },

    tabItems() {
      return [{ title: this.stockOutTitle }, { title: this.stockOutHistoryTitle }];
    },
  },
  data: function () {
    return {
      partInventoryTableData: [],
      partInventoryTableDataType: {
        '#': 'number',
        'Job No': 'string',
        Date: 'datetime',
        Warehouse: 'string',
        Vehicle: 'string',
        'Purchase From': 'string',
        'DO #': 'string',
        'Billing Type': 'string',
        Remark: 'string',
        'Last Updated': 'datetime',
        'Updated By': 'string',
        CustRef: 'string',
        JobStatus: 'string',
      },
      partInventoryTableSelectedRows: [],
      partInventoryTableLoading: true,
      partInventoryTableColumnOrderViews: [],
      partInventoryTableColumnOrderID: null,

      popupEntryShow: false,
      entryRequired: [],
      entrySuccess: [],
      entryError: [],
      viewMode: false,

      jobNoValue: '',
      lastUpdatedValue: '',

      jobDateValue: null,
      vehicleNoValue: null,
      doNoValue: '',
      warehouseValue: null,
      contNoValue: null,
      sizeValue: null,
      sizeInputValue: '',
      sealNoValue: '',
      billingTypeValue: null,
      remarkValue: '',

      jobDateValueError: '',
      warehouseValueError: '',

      vehicleList: [],
      warehouseList: [],
      sizeList: [{ size: '20' }, { size: '40' }, { size: '45' }], //i dont understand this sizes, but it's the hardcoded set from 4G
      billingTypeList: [],

      partListTableData: [],
      partListTableSelectedRows: [],
      partListTableColumnOrderViews: [],
      partListTableColumnOrderID: null,
      partListTableDataType: {
        '#': 'number',
        'Serial No': 'string',
        'Part Type': 'string',
        'Part No': 'string',
        'Mfg Date': 'date',
        Manufacturer: 'string',
        'Made In': 'string',
        Model: 'string',
        Qty: 'number',
        QtyBal: 'number',
        'Unit Price(RM)': 'currency',
        'Amount(RM)': 'currency',
        'Purchase From': 'string',
        'Date Purchase': 'date',
        Location: 'string',
        '# Of Retread': 'number',
        Size: 'string',
        Remark: 'string',
        'Supp Invoice': 'string',
        'PO #': 'string',
      },
      purchaseOrderList: [],
      purchaseOrderListLoading: false,
      purchaseOrderValue: null,
      purchaseOrderImportedLabel: '',
      purchaseOrderValueError: '',
      purchaseOrderImportDetails: null,
      purchaseOrderDataType: {
        '#': 'number',
        'PO #': 'string',
        'Ref #': 'string',
        'Purchase Date': 'date',
        Supplier: 'string',
        Status: 'string',
        'Job Type': 'string',
        'Total Value': 'currency',
        'Date Updated': 'datetime',
        'Updated By': 'string',
      },
      tabsFieldsAction: '',

      partListSelectedItem: null,

      partFields: {
        partTypeValue: null,
        serialNoValue: '',
        partNoValue: '',
        sizeValue: '',
        unitPriceValue: '',
        qtyValue: '',
        modelValue: '',
        locationValue: null,
        madeInValue: null,
        manufacturerValue: null,
        mfgDateValue: null,
        poNoValue: '',
        supplierValue: null,
        sppInvValue: '',
        remarkValue: '',

        tyreRetreadValue: false,
        noOfRetreadValue: '',
      },

      partTypeValueError: '',
      serialNoValueError: '',
      noOfRetreadValueError: '',
      qtyValueError: '',

      retreadError: '',

      partFieldsChangedCheck: {},

      partTypeList: [],
      manufacturerList: [],
      countryList: [],
      supplierList: [],
      locationList: [],

      popupAction: '',

      SASControl: [],

      stockOutPopupShow: false,
      stockOutUpdatePopupShow: false,

      selectedTabIndex: 0,

      stockOutList: [],
      stockOutHistoryList: [],

      selectedStockOutSelectedItem: '',

      stockOutDateValue: null,
      stockOutVehicleNo: '',
      stockOutRemark: '',
      stockOutQuantity: '',
      stockOutQuantityError: '',

      stockOutDateValueError: '',
      stockOutRemarkError: '',

      stockOutListDataType: {
        '#': 'number',
        'GRN No': 'string',
        'GRN Date': 'datetime',
        'GIT No': 'string',
        'GIT Date': 'datetime',
        'Part Type': 'string',
        'Serial No': 'string',
        Qty: 'number',
        'Qty Bal': 'number',
        Warehouse: 'string',
        'Part No': 'string',
        Model: 'string',
        Size: 'string',
        Manufacturer: 'string',
        'Made In': 'string',
        'Supp Invoice': 'string',
        'PO No': 'string',
        'GRN Guid': 'string',
      },
    };
  },
  props: {},
  methods: {
    async menuClicked(e) {
      this.viewMode = false;
      this.popupAction = '';
      this.clearFields();
      switch (e.key) {
        case 'New': {
          await this.getPurchaseOrderList();
          this.popupEntryShow = true;
          this.popupAction = 'N';
          break;
        }
        case 'Edit': {
          if (this.partInventoryTableSelectedRows.length < 1) {
            this.$showAlert('error', 'No Part Inventory is selected', 'Please select a Part Inventory to Edit');
            break;
          }
          const { 'Job No': jobNo } = this.partInventoryTableSelectedRows[0];
          this.populatePartInventoryFields(jobNo);
          this.popupEntryShow = true;
          this.popupAction = 'E';
          break;
        }
        case 'View': {
          if (this.partInventoryTableSelectedRows.length < 1) {
            this.$showAlert('error', 'No Part Inventory is selected', 'Please select a Part Inventory to View');
            break;
          }
          const { 'Job No': jobNo } = this.partInventoryTableSelectedRows[0];
          this.populatePartInventoryFields(jobNo);
          this.viewMode = true;
          this.popupEntryShow = true;
          break;
        }
        case 'Delete': {
          if (this.partInventoryTableSelectedRows.length < 1) {
            this.$showAlert('error', 'No Part Inventory is selected', 'Please select a Part Inventory to Delete');
            break;
          }
          const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Part Inventory?', '', [
            { text: 'Delete', color: 'Red', textColor: 'white' },
            { text: 'Cancel' },
          ]);
          if (discard == 'Cancel') return false;
          this.$fullscreenLoading('Deleting Part Inventory');

          const { 'Job No': jobNo } = this.partInventoryTableSelectedRows[0];
          const result = (
            await this.$apiCallV2('/PartInventory/SavePartInventory', {
              jobno: jobNo,
              action: 'D',
            })
          )[0];
          const { Results, ErrMsg } = result || {};

          if (Results == '0') {
            this.$showAlert('success', 'Part Inventory Deleted', ErrMsg, [{ text: 'OK' }]);
          } else {
            this.$showAlert('error', 'Part Inventory Could Not Be Deleted', ErrMsg, [{ text: 'OK' }]);
          }
          await this.getPartInventory();
          this.$fullscreenLoading();
          break;
        }
        case 'PrintStockBalance': {
          const print = this.$router.resolve(`/Home/${this.$route.params.user}/Workshop/PrintStockBalance`);

          window.open(print.href, '_blank');
          break;
        }
        case 'StockOut': {
          this.stockOutPopupShow = true;
          this.$fullscreenLoading('Getting Stock Out...');

          await this.getStockOutGRN();
          await this.getStockOutHistory();

          this.$fullscreenLoading();
          break;
        }
      }
    },

    async getSAS() {
      this.SASControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=EMS&page=Tyre Storage`);
    },

    async getVehicleList() {
      const result = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'VEHICLE' })).map((item) => ({
        ...item,
        label: `${item['Inventory #']} - ${item['Inventory Code']}`,
      }));
      this.vehicleList = result;
    },

    async getWarehouseList() {
      const result = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'WAREHOUSE' })).map((item) => ({
        ...item,
        label: `${item['Warehouse Code']} - ${item['Name']}`,
      }));
      this.warehouseList = result;
    },

    async getBillingType() {
      const result = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PARTINVENTORYBILLINGTYPE' })).map(
        (item) => ({
          ...item,
          label: `${item['billType']} - ${item['Description']}`,
        }),
      );
      this.billingTypeList = result;
    },

    async getPartInventoryProductColumnOrder() {
      if (this.partListTableColumnOrderID !== null) return;

      const columnOrderID = {
        formname: 'EMS_PartInventory',
        formctrlname: 'Part Inventory Products',
      };
      this.partListTableColumnOrderViews = await this.$apiCallV2('/TableBox/GetColumnOrder', columnOrderID);
      this.partListTableColumnOrderID = columnOrderID;
    },

    async getPartInventory() {
      this.partInventoryTableLoading = true;
      this.partInventoryTableData = [];
      await this.$nextTick();

      const result = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PARTINVENTORY' })).map(
        (partinv, index) => {
          const { 'Billing Type': billType } = partinv;
          const { label } = this.billingTypeList.find((bill) => bill.billType == billType) || {};
          const billTypeDesc = label || billType;
          return {
            '#': index + 1,
            ...partinv,
            'Billing Type': billTypeDesc,
          };
        },
      );

      const columnOrderID = {
        formname: 'EMS_PartInventory',
        formctrlname: 'Part Inventory',
      };
      if (this.partInventoryTableColumnOrderID === null) {
        this.partInventoryTableColumnOrderViews = await this.$apiCallV2('/TableBox/GetColumnOrder', columnOrderID);
        this.partInventoryTableColumnOrderID = columnOrderID;
      }

      this.partInventoryTableData = result;
      this.partInventoryTableLoading = false;
    },

    async getPartType() {
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PARTTYPEWITHPRODUCTINFO' });
      this.partTypeList = result.map((item) => {
        const { 'Part Type': PartType, Description, 'Part Group': PartGroup, ProdName, Size, Shade } = item;

        const partTypeLabel = `${PartType}${PartGroup ? ` [${PartGroup}]` : ''}`;
        const infoArray = [];
        if (ProdName && ProdName != Description) infoArray.push(ProdName);
        if (Size && Size != '-') infoArray.push(Size);
        if (Shade && Shade != '-') infoArray.push(Shade);
        const infoLabel = infoArray.length > 0 ? `[${infoArray.join('|')}]` : '';
        const descriptionLabel = `${Description} ${infoLabel}`;

        return {
          ...item,
          label: `${partTypeLabel} - ${descriptionLabel}`,
        };
      });
    },

    async getCountry() {
      this.countryList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'country' })).map((item) => ({
        ...item,
        label: `${item['Country Code']} - ${item.Country}`,
      }));
    },
    async getManufacturer() {
      this.manufacturerList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'manufacturer' })).map(
        (item) => ({ ...item, label: `${item.MFGCode} - ${item.MFGName}` }),
      );
    },

    async getSupplierList() {
      this.supplierList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SUPPLIER' })).map((item) => ({
        ...item,
        label: `${item['Supplier Code']} - ${item['Supplier Name']}`,
      }));
    },

    async getLocation() {
      this.locationList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'LOCATION' });
    },

    async getPurchaseOrderList() {
      this.purchaseOrderListLoading = true;
      try {
        const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PURCHASEORDER' });
        this.purchaseOrderList = result.map((item, index) => ({
          ...item,
          '#': index + 1,
          label: `${item.PONO} ${item.RefNo ? `(${item.RefNo})` : ''}`,
          'PO #': item.PONO,
          'Ref #': item.RefNo,
          'Purchase Date': item.PODate,
          Supplier: `${item.Supplier || ''}${item.Spp_Name ? ` - ${item.Spp_Name}` : ''}`,
          Status: item.Status,
          'Job Type': item.JobDescription ?? '',
          'Total Value': item.TotalValue,
          'Date Updated': item.dateUpdate,
          'Updated By': item.UserID,
        }));
      } finally {
        this.purchaseOrderListLoading = false;
      }
    },

    async getPurchaseOrderDetails(PONo) {
      const result = await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'PURCHASEORDER',
        recordid: PONo,
      });
      return result.PO;
    },

    clearFields() {
      this.jobNoValue = '';
      this.lastUpdatedValue = '';
      this.jobDateValue = null;
      this.vehicleNoValue = null;
      this.doNoValue = '';
      this.warehouseValue = null;
      this.contNoValue = null;
      this.sizeValue = null;
      this.sealNoValue = '';
      this.billingTypeValue = null;
      this.remarkValue = '';
      this.purchaseOrderValue = null;
      this.purchaseOrderImportedLabel = '';
      this.purchaseOrderValueError = '';
      this.clearPurchaseOrderImportDetails();

      this.clearPopupErrors();

      this.partListSelectedItem = null;
      this.partListTableData = [];
      this.clearPartFields();

      this.clearEntryTexts();
    },

    clearPurchaseOrderImportDetails() {
      this.purchaseOrderImportDetails = {
        po: null,
        items: [],
      };
    },

    async loadPurchaseOrderImportDetails(PONo) {
      this.$fullscreenLoading('Getting Purchase Order Details...');
      const purchaseOrder = await this.getPurchaseOrderDetails(PONo);
      this.$fullscreenLoading();

      if (!purchaseOrder) {
        this.clearPurchaseOrderImportDetails();
        this.$showAlert('error', 'Purchase Order Could Not Be Loaded', '');
        return;
      }

      this.purchaseOrderImportDetails = {
        po: purchaseOrder,
        items: this.convertToArray(purchaseOrder.PODetail),
      };
    },

    async purchaseOrderFocusChanged(focused) {
      if (!focused) return;

      await this.getPurchaseOrderList();
    },

    async importPurchaseOrderItems() {
      if (this.fieldsChanged) {
        const discard = await this.cancelEdit();
        if (!discard) return;
      }

      const { PONO } = this.purchaseOrderValue || {};
      if (!PONO) {
        this.purchaseOrderValueError = 'Purchase Order is required!';
        return;
      }

      await this.loadPurchaseOrderImportDetails(PONO);

      const { po, items } = this.purchaseOrderImportDetails || {};
      const { PONO: PONo, Supplier: supplier, RefNo: poRefNo } = po || {};

      if (!PONo) {
        this.purchaseOrderValueError = 'Purchase Order is required!';
        return;
      }

      if (items.length < 1) {
        this.$showAlert('error', 'No PO Items', 'Selected Purchase Order has no items to import.');
        return;
      }

      const unavailableItems = items.filter((item) => this.isPurchaseOrderImportItemUnavailable(item));
      const availableItems = items.filter((item) => !this.isPurchaseOrderImportItemUnavailable(item));

      if (unavailableItems.length > 0) {
        const itemList = unavailableItems
          .map((item) => item.ProdCode || item.Name || item.SeqNo)
          .filter(Boolean)
          .join('<br>');
        await this.$showAlert(
          'warning',
          'Unavailable PO Items Skipped',
          `Some PO items are not configured in EMS Part Type and will not be imported.${itemList ? `<br><br>${itemList}` : ''}`,
          [{ text: 'OK' }],
        );
      }

      if (availableItems.length < 1) {
        return;
      }

      let nextSeqNo = this.partListTableData.reduce(
        (max, item) => (item.DNSeqNo !== undefined ? Math.max(max, item.DNSeqNo) : max),
        0,
      );

      const importItems = [];
      availableItems.forEach((item) => {
        const { ItemTxnID, Quantity, QuantityBal, UnitPrice, Name, Remark } = item;
        const importProdTxnId = ItemTxnID;
        const partType = this.partTypeList.find((part) => part.ProdTxnID == importProdTxnId);
        const importQuantityParse = parseFloat(QuantityBal || Quantity || '1');
        if (!importQuantityParse || importQuantityParse <= 0) return;
        const importQuantity = isNaN(importQuantityParse) ? '0.00' : importQuantityParse.toFixed(2);
        const uniqueCodeQuantity = partType?.['Unique Code'] == 'Y' ? Math.max(Math.floor(parseFloat(importQuantity) || 0), 1) : 1;

        for (let index = 0; index < uniqueCodeQuantity; index += 1) {
          nextSeqNo += 1;
          importItems.push({
            ProdTxnID: importProdTxnId,
            BatchNo: '',
            PalletNo: '',
            Size: partType?.Size ?? '',
            UnitPrice: UnitPrice || '0.00',
            Quantity: partType?.['Unique Code'] == 'Y' ? '1.00' : importQuantity,
            CalcQtyBal: partType?.['Unique Code'] == 'Y' ? '1.00' : importQuantity,
            ProdDesc: Name || '',
            BinLoc: '',
            MadeIn: '',
            Manufacturer: '',
            ProdDate: null,
            PONo: PONo,
            Spp_Code: supplier ?? '',
            SppInv: '',
            Remark2: Remark ?? '',
            NoOfRetread: '0',
            DNSeqNo: nextSeqNo,
            action: 'N',
          });
        }
      });

      if (importItems.length < 1) {
        this.$showAlert('error', 'No PO Items', 'Selected Purchase Order has no items with quantity to import.');
        return;
      }

      const updatePartList = [...this.partListTableData, ...importItems];
      this.partListTableData = [];
      this.$nextTick(() => {
        this.partListTableData = updatePartList;
      });
      if (poRefNo) this.remarkValue = poRefNo;
      this.purchaseOrderImportedLabel = `Imported from PO: ${PONo}${poRefNo ? ` (${poRefNo})` : ''}`;
      this.purchaseOrderValue = null;
      this.clearPurchaseOrderImportDetails();
    },

    async clearPurchaseOrderImport() {
      if (this.fieldsChanged) {
        const discard = await this.cancelEdit();
        if (!discard) return;
      }

      const clearImport = await this.$showAlert(
        'warning',
        'Clear Import',
        'This will clear all items in the Product List.',
        [
          { text: 'Clear Import', color: 'Red', textColor: 'white' },
          { text: 'Cancel' },
        ],
      );
      if (clearImport == 'Cancel') return;

      this.partListTableData = [];
      this.partListTableSelectedRows = [];
      this.remarkValue = '';
      this.purchaseOrderValue = null;
      this.purchaseOrderImportedLabel = '';
      this.purchaseOrderValueError = '';
      this.clearPurchaseOrderImportDetails();
    },

    isPurchaseOrderImportItemUnavailable(item) {
      const { ItemTxnID } = item || {};
      return !this.partTypeList.find((part) => part.ProdTxnID == ItemTxnID);
    },

    clearPartFields() {
      this.partListSelectedItem = null;

      this.partFields.partTypeValue = null;
      this.partFields.serialNoValue = '';
      this.partFields.partNoValue = '';
      this.partFields.sizeValue = '';
      this.partFields.unitPriceValue = '';
      this.partFields.qtyValue = '';
      this.partFields.modelValue = '';
      this.partFields.locationValue = null;
      this.partFields.madeInValue = null;
      this.partFields.manufacturerValue = null;
      this.partFields.mfgDateValue = null;
      this.partFields.poNoValue = '';
      this.partFields.supplierValue = null;
      this.partFields.sppInvValue = '';
      this.partFields.remarkValue = '';

      this.partFields.tyreRetreadValue = false;
      this.partFields.noOfRetreadValue = '';

      this.clearPartFieldsError();
    },

    clearPartFieldsError() {
      this.partTypeValueError = '';
      this.serialNoValueError = '';
      this.qtyValueError = '';

      this.retreadError = '';
      this.noOfRetreadValueError = '';
    },

    clearPopupErrors() {
      this.jobDateValueError = '';
      this.warehouseValueError = '';
    },

    clearEntryTexts() {
      this.entryRequired = [];
      this.entrySuccess = [];
      this.entryError = [];
    },

    async getPartInventoryDetails(jobNo) {
      this.$fullscreenLoading('Getting Part Inventory Details...');
      const result = await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'PARTINVENTORY',
        recordid: jobNo,
      });
      this.$fullscreenLoading();
      return result.PartInventory;
    },

    async populatePartInventoryFields(jobNo) {
      const partInventory = await this.getPartInventoryDetails(jobNo);
      const {
        JobNo,
        JobDate,
        DoNO,
        WhseCode,
        VehicleNo,
        ContNo,
        Size,
        SealNo,
        BillingType,
        Remark,
        LastUpdated,
        UpdatedBy,

        PartList,
      } = partInventory || {};

      this.jobNoValue = JobNo;
      this.lastUpdatedValue = `${UpdatedBy} ${new Date(LastUpdated).formatString('dd/MM/yyyy HH:mm')}`;

      this.jobDateValue = JobDate ? new Date(JobDate) : null;
      this.vehicleNoValue = VehicleNo ? this.vehicleList.find((item) => item['Inventory Code'] == VehicleNo) : null;
      this.doNoValue = DoNO ?? '';
      this.warehouseValue = WhseCode ? this.warehouseList.find((item) => item['Warehouse Code'] == WhseCode) : null;
      this.contNoValue = ContNo ?? '';
      this.sizeInputValue = Size ?? '';
      this.sealNoValue = SealNo ?? '';
      this.billingTypeValue = BillingType ? this.billingTypeList.find((item) => item['billType'] == BillingType) : null; //right now old data uses the Description of this BillingType, which is wrong. Moving forward use billingType for this.
      this.remarkValue = Remark ?? '';

      await this.getPartInventoryProductColumnOrder();
      this.partListTableData = [];
      await this.$nextTick();
      this.partListTableData =
        PartList ? this.convertToArray(PartList).map((item) => ({ ...item, action: 'E' })) : [];

      let findMaxSeqNo = this.partListTableData.reduce(
        (max, item) => (item.DNSeqNo !== undefined ? Math.max(max, item.DNSeqNo) : max),
        0,
      );

      this.partListTableData = this.partListTableData.map((item) => {
        if (item.DNSeqNo === undefined) {
          findMaxSeqNo += 1; // increment max for each missing DNSeqNo
          return { ...item, DNSeqNo: findMaxSeqNo };
        }
        return item;
      });
      //always set action to 'E' initially because there might be chabnges to the top fields that need editing. Should make a dedicated check for it but this is simplier for now.
    },

    populateProductFields(product) {
      const {
        ProdTxnID,
        BatchNo,
        PalletNo,
        ProdDesc,
        Manufacturer,
        MadeIn,
        Size,
        UnitPrice,
        Quantity,
        ProdDate,
        Spp_Code: SppCode,
        SppInv,
        PONo,
        Remark2,
        BinLoc,
        NoOfRetread,
      } = product;

      this.partFields.partTypeValue = this.partTypeList.find((item) => item.ProdTxnID == ProdTxnID) ?? null;
      this.partFields.serialNoValue = BatchNo ?? '';
      this.partFields.partNoValue = PalletNo ?? '';
      this.partFields.sizeValue = Size ?? '';
      this.partFields.unitPriceValue = UnitPrice ? this.$currencyFormat(UnitPrice) : '';
      this.partFields.qtyValue = Quantity ? parseFloat(Quantity).toFixed(2) : '';
      this.partFields.modelValue = ProdDesc ?? '';
      this.partFields.locationValue = this.locationListFiltered.find((item) => item.Location == BinLoc) ?? null;
      this.partFields.madeInValue = this.countryList.find((item) => item['Country Code'] == MadeIn) ?? null;
      this.partFields.manufacturerValue = this.manufacturerList.find((item) => item.MFGCode == Manufacturer) ?? null;
      this.partFields.mfgDateValue = ProdDate ? new Date(ProdDate) : null;
      this.partFields.poNoValue = PONo ?? '';
      this.partFields.supplierValue = this.supplierList.find((item) => item['Supplier Code'] == SppCode) ?? null;
      this.partFields.sppInvValue = SppInv ?? '';
      this.partFields.remarkValue = Remark2 ?? '';

      //update these fields regardless of type.
      this.partFields.noOfRetreadValue = NoOfRetread ?? '';
      this.partFields.tyreRetreadValue = Boolean(NoOfRetread);
    },

    async tableRowClicked(_buttonText, rowClicked) {
      if (this.fieldsChanged && !this.viewMode) {
        const discard = await this.cancelEdit();
        if (!discard) return;
      }
      const { DNSeqNo } = rowClicked;
      const product = this.partListTableData.find((item) => item.DNSeqNo == DNSeqNo);
      this.populateProductFields(product);
      this.partListTableSelectedRows = [rowClicked];
      this.partListSelectedItem = product;
      this.partFieldsChangedCheck = structuredClone(this.partFields);
      this.tabsFieldsAction = 'E';
    },

    partFieldsNew() {
      this.clearPartFields();
      this.partFieldsChangedCheck = structuredClone(this.partFields);
      this.partListSelectedItem = {};
      this.tabsFieldsAction = 'N';
    },

    partFieldsCancel() {
      this.clearPartFields();
      this.partListSelectedItem = null;
    },

    async partDeleteClick() {
      const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Product?', '', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;
      const { DNSeqNo } = this.partListTableSelectedRows[0] || {};
      const { action } = this.partListTableData.find((item) => item.DNSeqNo == DNSeqNo) || {};
      const update =
        action == 'N' ?
          this.partListTableData.filter((item) => item.DNSeqNo != DNSeqNo)
        : this.partListTableData.map((item) => {
            if (item.DNSeqNo == DNSeqNo) {
              return { ...item, action: 'D' };
            }
            return item;
          });
      this.partListTableData = [];
      this.$nextTick(() => {
        this.partListTableData = update;
      });
    },

    async tabFieldsSaveClicked() {
      const {
        partTypeValue,
        serialNoValue,
        partNoValue,
        sizeValue,
        unitPriceValue,
        qtyValue,
        modelValue,
        locationValue,
        madeInValue,
        manufacturerValue,
        mfgDateValue,
        poNoValue,
        supplierValue,
        sppInvValue,
        remarkValue,
        noOfRetreadValue,
      } = this.partFields || {};
      const { GRNGuid, DNSeqNo, CalcQtyBal, action } = this.partListSelectedItem;

      let error = false;

      if (!partTypeValue) {
        this.partTypeValueError = 'Part Type is required!';
        error = true;
      }

      if (this.serialNoRequired) {
        if (!serialNoValue) {
          this.serialNoValueError = 'Serial No is required!';
          error = true;
        }
      }

      if (serialNoValue) {
        const { BatchNo } = this.partListSelectedItem || {};
        if (BatchNo != serialNoValue) {
          const { Results, ErrMsg } = (await this.checkSerialNo(serialNoValue)) || {};
          if (Results != '1') {
            if (this.tyreRetreadEnable && !this.partFields.tyreRetreadValue)
              this.retreadError = 'If this is a retread tyre, please fill in retread info.';
            this.serialNoValueError = ErrMsg || 'Serial No is invalid!';
            error = true;
          }

          const serialNoExists = this.partListTableData.find((item) => item['BatchNo'] == serialNoValue);
          if (serialNoExists) {
            this.serialNoValueError = 'Serial No already exists in the part list!';
            error = true;
          }
        }
      }

      if (this.partFields.tyreRetreadValue && !this.partFields.noOfRetreadValue) {
        this.noOfRetreadValueError = 'No of Retread is required!';
        error = true;
      }

      if (GRNGuid) {
        if (parseFloat(qtyValue) < parseFloat(CalcQtyBal)) {
          this.qtyValueError = `Qty cannot be less than the issued qty of ${CalcQtyBal}`;
          error = true;
        }
      }

      if (error) return;

      if (this.partFields.qtyValue) {
        const parseQtyValue = parseFloat(this.partFields.qtyValue);
        this.partFields.qtyValue = isNaN(parseQtyValue) ? '0.00' : parseQtyValue.toFixed(2);
      }

      const findMaxSeqNo = this.partListTableData.reduce(
        (max, item) => (item.DNSeqNo !== undefined ? Math.max(max, item.DNSeqNo) : max),
        0,
      );

      const saveItem = {
        ...this.partListSelectedItem,
        ProdTxnID: partTypeValue?.ProdTxnID ?? '',
        BatchNo: serialNoValue ?? '',
        PalletNo: partNoValue ?? '',
        Size: sizeValue ?? '',
        UnitPrice: unitPriceValue || '0.00',
        Quantity: isNaN(parseFloat(qtyValue || '1')) ? '0.00' : parseFloat(qtyValue || '1').toFixed(2),
        ProdDesc: modelValue ?? '',
        BinLoc: locationValue?.Location ?? '',
        MadeIn: madeInValue?.['Country Code'] ?? '',
        Manufacturer: manufacturerValue?.MFGCode ?? '',
        ProdDate: mfgDateValue ? new Date(mfgDateValue) : null,
        PONo: poNoValue ?? '',
        Spp_Code: supplierValue?.['Supplier Code'] ?? '',
        SppInv: sppInvValue ?? '',
        Remark2: remarkValue ?? '',
        NoOfRetread: noOfRetreadValue || '0',
        DNSeqNo: DNSeqNo || findMaxSeqNo + 1,
        action: action == 'N' ? 'N' : this.tabsFieldsAction,
      };

      let updatePartList = this.partListTableData;
      if (this.tabsFieldsAction == 'N') {
        updatePartList = [...updatePartList, saveItem];
      } else if (this.tabsFieldsAction == 'E') {
        updatePartList = updatePartList.map((part) => {
          if (part.DNSeqNo == DNSeqNo) return saveItem;
          return part;
        });
      }
      this.partListTableData = [];
      this.$nextTick(() => {
        this.partListTableData = updatePartList;
      });
      this.clearPartFields();
    },

    async checkSerialNo(serialNo) {
      this.$fullscreenLoading('Checking Serial No...');
      const results = await this.$apiCallV2('/PartInventory/CheckSerialNo', { serialno: serialNo });
      this.$fullscreenLoading();
      return results[0];
    },

    async saveClicked() {
      if (this.fieldsChanged) {
        const discard = await this.cancelEdit();
        if (!discard) return;
      }

      const partListToSend = this.partListTableData.filter((item) => Boolean(item.action));

      this.clearEntryTexts();
      const error = [];

      if (!this.jobDateValue) {
        this.jobDateValueError = 'Date is required!';
        error.push({ key: 'jobdate', value: 'Date is required!' });
      }
      if (!this.warehouseValue) {
        this.warehouseValueError = 'Warehouse is required!';
        error.push({ key: 'warehouse', value: 'Warehouse is required!' });
      }

      if (partListToSend.length < 1) {
        error.push({ key: 'partlist', value: 'At least 1 Part is required!' });
      }

      const missingSerialNoRows = this.partListTableDataFiltered
        .filter((row) => {
          const part = this.partListTableData.find((item) => item.DNSeqNo == row.DNSeqNo);
          const partType = this.partTypeList.find((item) => item.ProdTxnID == part?.ProdTxnID);
          return partType?.['Unique Code'] == 'Y' && !part?.BatchNo;
        })
        .map((row) => `#${row['#']} ${row['Part Type']}`);

      if (missingSerialNoRows.length > 0) {
        await this.$showAlert(
          'warning',
          'Missing Serial No',
          `Serial No required for:<br>${missingSerialNoRows.join('<br>')}`,
          [{ text: 'OK' }],
        );
        return;
      }

      this.entryRequired = error;
      if (error.length > 0) return;

      this.$fullscreenLoading('Saving Part Inventory...');
      const requestBody = {
        action: this.popupAction,
        jobno: this.jobNoValue,
        jobdate: this.jobDateValue?.formatString('yyyy-MM-ddTHH:mm:ss') ?? null,
        jobdatedate: this.jobDateValue?.formatString('yyyy-MM-dd') ?? '',
        jobdatetime: this.jobDateValue?.formatString('HH:mm:ss') ?? '',
        remarks: this.remarkValue ?? '',

        vehiclecode: this.vehicleNoValue?.['Inventory Code'] ?? '',
        contno: this.contNoValue ?? '',
        sealno: this.sealNoValue ?? '',
        contsize: this.sizeInputValue || null,
        billtype: this.billingTypeValue?.['billType'] ?? '',
        dono: this.doNoValue ?? '',
        whsecode: this.warehouseValue?.['Warehouse Code'] ?? '',

        parts: partListToSend.map((item) => {
          const {
            GRNGuid,
            ProdTxnID,
            Quantity,
            Remark2,
            PONo,
            BinLoc,
            DNSeqNo,
            CustRefNo,
            Spp_Code: SppCode,
            SppInv,
            ProdDesc,
            MadeIn,
            Manufacturer,
            UnitPrice,
            NoOfRetread,
            PalletNo,
            BatchNo,
            ProdDate,
            SReqDate,
            EReqDate,
            CalcQtyBal,
            action,

            AllocGuid,
            PutAwayNo,
          } = item;

          return {
            grnguid: GRNGuid ?? null,
            prodtxnid: ProdTxnID,
            quantity: Quantity || '1',
            remark2: Remark2 ?? '',
            pono: PONo ?? '',
            location: BinLoc ?? '',
            dnseqno: DNSeqNo,
            custrefno: CustRefNo ?? '',
            supplier: SppCode ?? '',
            suppinv: SppInv ?? '',
            model: ProdDesc ?? '',
            madein: MadeIn ?? '',
            manufacturer: Manufacturer ?? '',
            unitprice: UnitPrice || '0.00',
            noofretread: NoOfRetread || '0',
            partno: PalletNo ?? '',
            serialno: BatchNo ?? '',
            mfgdate: ProdDate ? new Date(ProdDate).formatString('yyyy-MM-ddTHH:mm:ss') : null,
            sreqdate: SReqDate ?? null,
            ereqdate: EReqDate ?? null,
            qtybal: CalcQtyBal || '0.00',
            partaction: action,

            allocguid: AllocGuid ?? null,
            putawayno: PutAwayNo ?? '',
          };
        }),
      };
      const result = await this.$apiCallV2('/PartInventory/SavePartInventory', requestBody);

      const { Results, ErrMsg, PARTINVENTORY } = result?.[0] || {};

      if (Results == '1') {
        this.entryError.push(ErrMsg);
        this.$fullscreenLoading();
        return;
      }
      this.populatePartInventoryFields(PARTINVENTORY);
      this.viewMode = true;
      this.entrySuccess.push(ErrMsg);
      this.getPartInventory();
      this.$fullscreenLoading();
    },

    entryRequiredClicked: function (item) {
      this.$refs[item].setFocus();
    },

    async cancelEdit(warningText = null) {
      if (!warningText) warningText = 'You have unsaved changes.<br>Do you wish to discard the changes?';
      const discard = await this.$showAlert('warning', 'Discard Changes', warningText, [
        { text: 'Discard', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);

      if (discard == 'Cancel') return false;
      return true;
    },

    warehouseSelected() {
      const { Location } = this.partFields.locationValue || {};
      const valid = this.locationListFiltered.find((item) => item.Location == Location);
      if (!valid) this.partFields.locationValue = null;
    },

    partTypeSelected() {
      const { partTypeValue } = this.partFields;
      const { Size } = partTypeValue;

      this.partFields.sizeValue = Size ?? '';
      this.partFields.qtyValue = '1.00'; //Items with serialNo can only have 1 instance
    },

    serialNoChanged(_oldVal, _newVal) {
      this.partFields.qtyValue = '1.00';
    },

    convertToArray(item) {
      if (!item) return [];
      return Array.isArray(item) ? item : [item];
    },

    async getStockOutGRN() {
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'STOCKOUTGRNDETAILS' });
      this.stockOutList = [];
      this.$nextTick(() => {
        this.stockOutList = result;
      });
    },
    async getStockOutHistory() {
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'STOCKOUTHISTORY' });
      this.stockOutHistoryList = [];
      this.$nextTick(() => {
        this.stockOutHistoryList = result;
      });
    },

    tabClick(_tabItem, tabIndex) {
      this.selectedTabIndex = tabIndex;
    },

    stockOutFieldsClear() {
      this.stockOutDateValue = new Date();
      this.stockOutRemark = '';
      this.stockOutQuantity = '';
      this.stockOutVehicleNo = '';
    },

    stockOutTableRowClicked(_buttonText, rowClicked) {
      this.stockOutFieldsClear();
      this.stockOutUpdatePopupShow = true;
      this.selectedStockOutSelectedItem = this.stockOutList.find((item) => item.AllocGuid == rowClicked.AllocGuid);
      this.stockOutQuantity = this.selectedStockOutSelectedItem.CalcQtyBal.toString();
    },

    async stockOutClick() {
      if (!this.selectedStockOutSelectedItem) return;

      let error = false;
      if (!this.stockOutDateValue) {
        this.stockOutDateValueError = 'Date is required';
        error = true;
      }

      if (!this.stockOutRemark) {
        this.stockOutRemarkError = 'Remark is required';
        error = true;
      }

      if (!this.stockOutQuantity) {
        this.stockOutQuantityError = 'Quantity is required';
        error = true;
      }

      if (error) return;

      this.$fullscreenLoading('Stock Out Processing...');

      const {
        AllocGuid,
        GRNGuid,
        JobNo,
        ProdTxnID,
        BatchNo,
        PalletNo,
        ProdDesc,
        Manufacturer,
        MadeIn,
        UnitPrice,
        PickQtyBal,
        ProdDate,
        SppCode,
        PONo,
        WHSECode,
        NoOfRetread,
        BinLoc,
        CalcQtyBal,
      } = this.selectedStockOutSelectedItem;

      const stockOutQuantityLimit =
        parseFloat(this.stockOutQuantity) > parseFloat(CalcQtyBal) ? CalcQtyBal : this.stockOutQuantity;

      const result = await this.$apiCallV2('/PartInventory/StockOut', {
        stockoutdate: new Date(this.stockOutDateValue).formatString('yyyy-MM-dd HH:mm'),
        remark: this.stockOutRemark,
        vehicleno: this.stockOutVehicleNo,
        allocguid: AllocGuid,
        stockoutquantity: stockOutQuantityLimit,
        action: 'N',
        grnguid: GRNGuid ?? '',
        jobno: JobNo ?? '',
        prodtxnid: ProdTxnID ?? '',
        batchno: BatchNo ?? '',
        palletno: PalletNo ?? '',
        proddesc: ProdDesc ?? '',
        manufacturer: Manufacturer ?? '',
        madein: MadeIn ?? '',
        unitprice: UnitPrice ?? '0',
        pickqtybal: PickQtyBal ?? '0',
        proddate: ProdDate,
        sppcode: SppCode,
        pono: PONo,
        whsecode: WHSECode,

        plno: '',
        noofretread: NoOfRetread,
        binloc: BinLoc,
        grnno: '',

        txnid: null,

        jobdatedate: new Date(this.stockOutDateValue).formatString('yyyy-MM-dd'),
        jobdatetime: new Date(this.stockOutDateValue).formatString('HHmm'),
      });
      const { ErrMsg, Results } = result?.[0] || {};

      this.stockOutUpdatePopupShow = false;

      if (Results == '0') {
        this.$showAlert('success', 'Stock Out', ErrMsg, [{ text: 'OK' }]);
        this.getStockOutGRN();
        this.getStockOutHistory();
      } else {
        this.$showAlert('error', 'Stock Out', ErrMsg, [{ text: 'OK' }]);
      }

      this.$fullscreenLoading();
    },

    async stockOutHistoryDeleteClicked(_buttonText, rowClicked) {
      const discard = await this.$showAlert('warning', 'Delete', 'Are you sure you want to delete this record?', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;

      const selectedGIT = this.stockOutHistoryList.find((item) => item.AllocGuid == rowClicked.AllocGuid);

      if (!selectedGIT) return;

      const {
        AllocGuid,
        GRNGuid,
        JobNo,
        ProdTxnID,
        BatchNo,
        PalletNo,
        ProdDesc,
        Manufacturer,
        MadeIn,
        UnitPrice,
        PickQtyBal,
        ProdDate,
        SppCode,
        PONo,
        WHSECode,
        PLNo,
        NoOfRetread,
        BinLoc,
        GRNNo,
        TxnID,
      } = selectedGIT;

      const result = await this.$apiCallV2('/PartInventory/StockOut', {
        stockoutdate: new Date(this.stockOutDateValue).formatString('yyyy-MM-dd HH:mm'),
        remark: this.stockOutRemark,
        vehicleno: this.stockOutVehicleNo,
        stockoutquantity: '0',
        allocguid: AllocGuid,
        action: 'D',
        grnguid: GRNGuid ?? '',
        jobno: JobNo ?? '',
        prodtxnid: ProdTxnID ?? '',
        batchno: BatchNo ?? '',
        palletno: PalletNo ?? '',
        proddesc: ProdDesc ?? '',
        manufacturer: Manufacturer ?? '',
        madein: MadeIn ?? '',
        unitprice: UnitPrice ?? '0',
        pickqtybal: PickQtyBal ?? '0',
        proddate: ProdDate,
        sppcode: SppCode,
        pono: PONo,
        whsecode: WHSECode,

        plno: PLNo,
        noofretread: NoOfRetread,
        binloc: BinLoc,
        grnno: GRNNo,

        txnid: TxnID,

        stockoutdatedate: new Date(this.stockOutDateValue).formatString('yyyy-MM-dd'),
        stockoutdatetime: new Date(this.stockOutDateValue).formatString('HHmm'),
      });
      const { ErrMsg, Results } = result?.[0] || {};

      this.stockOutUpdatePopupShow = false;

      if (Results == '0') {
        this.$showAlert('success', 'Stock Out', ErrMsg, [{ text: 'OK' }]);
        this.getStockOutGRN();
        this.getStockOutHistory();
      } else {
        this.$showAlert('error', 'Stock Out', ErrMsg, [{ text: 'OK' }]);
      }

      this.$fullscreenLoading();
    },

    openFromParams() {
      const params = new URLSearchParams(window.location.search);

      const newValue = params.get('New');
      if (newValue != null) this.menuClicked({ key: 'New' });
    },
  },
  async mounted() {
    const promiseList = [
      this.getSAS(),
      this.getVehicleList(),
      this.getWarehouseList(),
      this.getBillingType(),
      this.getPartType(),
      this.getCountry(),
      this.getManufacturer(),
      this.getSupplierList(),
      this.getLocation(),
      this.getPurchaseOrderList(),
      this.getPartInventoryProductColumnOrder(),
    ];
    let finished = 0;
    for (const p of promiseList) {
      p.then(() => {
        finished++;
        this.$topLoading((finished / promiseList.length) * 0.9);
      });
    }
    await Promise.all(promiseList);

    await this.getPartInventory();
    this.$topLoading(1);

    this.openFromParams();
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  > .right-container {
    margin-left: 5px;
    padding: 5px;
    width: 100%;
    height: 100%;
    background-color: white;
    overflow: hidden;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
  }
}

.flex-container {
  display: flex;
  gap: 8px;
  width: 100%;

  > * {
    width: 100%;
  }
}

.table-tab-container {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 10px;
  height: 100%;

  > *:only-child {
    grid-column: 1/-1;
  }

  > * {
    min-width: 0;
    min-height: 0;
  }

  .table-box-main {
    min-width: 0;
    min-height: 0;
  }

  .cta-group {
    display: flex;
    gap: 5px;
    justify-content: flex-start;
    margin-bottom: 10px;
    align-items: center;
  }

  .po-import-cta,
  .part-list-cta {
    display: flex;
    gap: 5px;
    align-items: center;
  }

  .part-list-cta {
    margin-left: auto;
  }

  .po-import-input {
    width: 420px;
    flex: 0 0 420px;
  }

  .po-imported-label {
    padding: 4px 8px;
    background: #f3f5f9;
    outline: 1px solid #cbd3e1;
    border-radius: 2px;
  }

  .table-container {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
  }
}

.stock-out-popup {
  position: absolute;
  z-index: 9999;
  background: rgba(0, 0, 0, 50%);
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  .stock-out-popup-inner {
    background: white;
    height: 100%;
    max-height: 680px;
    width: 100%;
    max-width: 1200px;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 5px;
    position: relative;
    top: 20px;
    padding: 8px 12px;
    border-radius: 5px;

    &.stock-out-action {
      max-width: 440px;
      max-height: fit-content;
      &:has(.calendar) {
        min-height: 400px;
      }
      &:has(.dropdown-list) {
        min-height: 480px;
      }
    }
  }
}

.picker-main {
  margin-left: 5px;
}
</style>
